import { Markdown } from '@cadstrom/sigma-engine-shared';
import ButtonBase from '@mui/material/ButtonBase';
import clsx from 'clsx';
import { useState } from 'react';

import { DirectiveApplyBar } from './directive-apply-bar.js';
import { PinListInput } from './pin-list-input.js';
import { useParsedDirectiveRows } from './use-parsed-directive-rows.js';

/** `configId` of the extraction context this table's rows apply into. */
export const SIGMA_MODEL_SIGNAL_PINS_CONFIG_ID = 'sigma-model-signal-pins';

export interface SigmaModelSignalPinsTableMarkdownItemProps {
	/** Raw JSON string containing the rows array. */
	jsonData?: string;
}

const editInputClass =
	'w-full bg-transparent border-b border-gray-a6 focus:outline-none focus:border-blue-9 px-1 py-0.5';

/**
 * Parses the directive body, keeping only well-formed rows and the first row for each row key.
 *
 * @param jsonData - Raw JSON body of the directive.
 * @returns The rows, or null when none parse.
 */
function parseRows(jsonData: string): Markdown.SigmaModelSignalPinsRow[] | null {
	try {
		const parsed: unknown = JSON.parse(jsonData);
		if (!Array.isArray(parsed)) {
			return null;
		}
		const seenKeys = new Set<string>();
		const rows = parsed.flatMap((item) => {
			const result = Markdown.SigmaModelSignalPinsRowSchema.safeParse(item);
			if (!result.success || seenKeys.has(result.data.rowKey)) {
				return [];
			}
			seenKeys.add(result.data.rowKey);
			return [result.data];
		});
		return rows.length > 0 ? rows : null;
	} catch {
		return null;
	}
}

/**
 * Markdown directive component that renders a proposal for the Sigma Model Entry Form's signal to
 * pin table: for each sigma model signal, what the datasheet calls it and which pins it is on.
 * Datasheet names and pins are editable inline before applying.
 *
 * @param props - The directive's raw JSON body.
 * @returns The rendered table, or null if `jsonData` is missing or cannot be parsed.
 */
export function SigmaModelSignalPinsTableMarkdownItem({ jsonData }: SigmaModelSignalPinsTableMarkdownItemProps) {
	const [rows, setRows] = useParsedDirectiveRows(jsonData, parseRows);
	const [isEditing, setIsEditing] = useState(false);

	if (!rows) {
		return null;
	}

	const showBlock = rows.some((row) => row.block !== null);

	function updateRow(index: number, patch: Partial<Markdown.SigmaModelSignalPinsRow>) {
		setRows((prev) => (prev ? prev.map((row, i) => (i === index ? { ...row, ...patch } : row)) : null));
	}

	return (
		<>
			<div className='my-2 overflow-x-auto rounded border border-gray-a4 text-sm'>
				<table className='w-full border-collapse'>
					<thead>
						<tr className='bg-gray-a2 text-left text-xs font-semibold text-gray-11'>
							{showBlock && <th className='px-3 py-2'>Device</th>}
							<th className='px-3 py-2'>Model signal</th>
							<th className='px-3 py-2'>Datasheet name</th>
							<th className='px-3 py-2'>Pins</th>
							<th className='px-3 py-2 text-right'>
								<ButtonBase
									className='font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
									onClick={() => setIsEditing((v) => !v)}
								>
									{isEditing ? 'Done' : 'Edit'}
								</ButtonBase>
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr key={row.rowKey} className={clsx('border-t border-gray-a3', index % 2 === 1 && 'bg-gray-a2')}>
								{showBlock && <td className='px-3 py-1.5 text-gray-11'>{row.block}</td>}
								<td className='px-3 py-1.5 font-mono font-medium'>{row.modelSignalName}</td>
								<td className='px-3 py-1.5'>
									{isEditing ?
										<input
											type='text'
											value={row.datasheetName ?? ''}
											onChange={(e) => updateRow(index, { datasheetName: e.target.value || null })}
											className={editInputClass}
										/>
									:	(row.datasheetName ?? <span className='text-gray-a9'>same as model</span>)}
								</td>
								<td className='px-3 py-1.5 text-gray-11'>
									{isEditing ?
										<PinListInput
											pins={row.pins}
											onChange={(pins) => updateRow(index, { pins })}
											className={editInputClass}
										/>
									: row.pins.length > 0 ?
										row.pins.join(', ')
									:	<span className='text-gray-a9'>none</span>}
								</td>
								<td />
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<DirectiveApplyBar data={rows} configId={SIGMA_MODEL_SIGNAL_PINS_CONFIG_ID} />
		</>
	);
}
